import {formatUserDefinedObjectAdditionalColumnValue} from "~/domainModels/userDefinedObjectAdditionalColumnValues";
import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {EMOTORAD__USER__DEALERSHIP__COLUMN_ID, isEmotoradWorkspace} from "~/domainModels/workspaceSpecific/emotorad";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {formatAmaronOptionIdForDisplay, resolveAmaronEventTemplateStatusColumnConfig} from "~/domainModels/workspaceSpecific/amaron";
import type {ActivityTimelineResolverContext} from "~/modules/activityTimeline/types/activityTimeline.types";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import {formatNotesValueForDisplay} from "~/modules/activityTimeline/utils/activityTimelineNotesUtils";
import {formatRichTextForActivityDisplay} from "~/modules/activityTimeline/utils/activityTimelineRichTextUtils";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import {
    flatDispositionToDisplayNameFormat,
    formatDate,
    formatDateTime,
    getUserDisplayName,
    getUserRole,
    type PlatformUserDisplayDetails,
} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {getEventContainerDispositionOptions} from "~/utilities/ottopilotSemanticColumns";
import type {GeographyContextData} from "~/contexts/geographyContext";

const GEOGRAPHY_REFERENCE_COLUMN_TYPES = new Set<string>([
    UdoColumnType.geographyCountries,
    UdoColumnType.geographyIndiaCities,
    UdoColumnType.geographyIndiaPincodes,
    UdoColumnType.geographyIndiaStates,
    UdoColumnType.geographyIndiaZones,
    UdoColumnType.geographyLatitude,
    UdoColumnType.geographyLongitude,
]);

export function resolveActivityUserDisplayName(
    userId: Uuid | null,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>,
): string {
    if (userId === null) {
        return "System";
    }
    const displayName = getUserDisplayName(userId, users, userTemplates, platformUserDetailsByUserId);
    if (displayName === "-" || displayName === "?") {
        //TODO: VALIDATE invalid user
        // console.log("resolveActivityUserDisplayName", displayName);
        // console.log("users", userId);
        // console.log(users.filter((user) => user.id === userId));
        return displayName;
    }
    return displayName;
}

export function resolveActivityUserRoleLabel(
    userId: Uuid | null,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
): string {
    if (userId === null) {
        return "";
    }
    const user = users.find((item) => item.id === userId);
    if (user === undefined) {
        return "";
    }
    return getUserRole(user.templateId, userTemplates);
}

export function resolveActivityUserShowroomName(
    userId: Uuid | null,
    workspace: Workspace,
    users: Array<User>,
    organizationalUnits: Array<OrganizationalUnit>,
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
): string | undefined {
    if (userId === null || isEmotoradWorkspace(workspace) !== true) {
        return undefined;
    }
    const user = users.find((item) => item.id === userId);
    if (user === undefined) {
        return undefined;
    }
    const rawDealership = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
    const dealershipId: string | null =
        typeof rawDealership === "string" && rawDealership.trim() !== ""
            ? rawDealership
            : Array.isArray(rawDealership) && rawDealership.length > 0
              ? (typeof rawDealership[0] === "string"
                    ? rawDealership[0]
                    : ((rawDealership[0] as Record<string, unknown>)?.id as string) ?? null)
              : null;
    if (dealershipId === null) {
        return undefined;
    }
    const ou = organizationalUnits.find((item) => item.id === dealershipId);
    if (ou === undefined) {
        return undefined;
    }
    const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
    if (ouTemplate === undefined) {
        return undefined;
    }
    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
}

function resolveEventTemplateForActivityTimeline(
    eventTemplateId: Uuid,
    context: ActivityTimelineResolverContext,
): OttopilotEventTemplate | undefined {
    return context.eventTemplates.find((template) => template.id === eventTemplateId);
}

export function resolveEventStatusDisplayForActivityTimeline(getAdditionalColumns: CapabilityUdoColumnResolver,
    eventTemplateId: Uuid,
    columnValues: Record<string, unknown>,
    context: ActivityTimelineResolverContext,
): string | undefined {
    const eventTemplate = resolveEventTemplateForActivityTimeline(eventTemplateId, context);
    if (eventTemplate === undefined) {
        return undefined;
    }

    const statusColumnConfig =
        eventTemplate.configuration.statusColumnConfig ??
        (context.workspaceSpecific?.tryAmaronLevelColumns === true
            ? resolveAmaronEventTemplateStatusColumnConfig(eventTemplateId)
            : undefined);
    if (statusColumnConfig === undefined) {
        return undefined;
    }

    const raw = columnValues[String(statusColumnConfig.columnId)];
    if (raw === null || raw === undefined || String(raw).trim() === "") {
        return undefined;
    }

    if (context.workspaceSpecific?.tryAmaronLevelColumns === true) {
        const amaronDisplay = formatAmaronOptionIdForDisplay(String(raw));
        if (amaronDisplay.trim() !== "") {
            return amaronDisplay;
        }
    }

    const formatted = formatActivityColumnFieldValue(getAdditionalColumns, raw, String(statusColumnConfig.columnId), {
        users: context.users,
        userTemplates: context.userTemplates,
        organizationalUnits: context.organizationalUnits,
        organizationalUnitTemplates: context.organizationalUnitTemplates,
        platformUserDetailsByUserId: context.platformUserDetailsByUserId,
        eventTemplateId: eventTemplateId,
        context: context,
    });
    if (formatted === "-" || formatted.trim() === "") {
        return undefined;
    }
    return formatted;
}

export function resolveDispositionDisplayName(getAdditionalColumns: CapabilityUdoColumnResolver,
    dispositionId: Uuid | null,
    eventContainerTemplate: EventContainerTemplate,
    userDefinedObjectOptionsByTemplateId: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>,
): string {
    if (dispositionId === null) {
        return "-";
    }
    const disposition = getEventContainerDispositionOptions(getAdditionalColumns, eventContainerTemplate, userDefinedObjectOptionsByTemplateId).find((item) => item.id === dispositionId);
    if (disposition === undefined) {
        return "?";
    }
    return flatDispositionToDisplayNameFormat(disposition.displayName);
}

export function resolveUtmSourceFromAdditionalColumnValues(
    additionalColumnValues: Record<string, unknown>,
    columnIds: Array<Uuid>,
): string | undefined {
    for (const columnId of columnIds) {
        const raw = additionalColumnValues[columnId];
        if (typeof raw === "string") {
            const trimmed = raw.trim();
            if (trimmed !== "") {
                return trimmed;
            }
        }
    }
    return undefined;
}

type ActivityFieldFormatOptions = {
    workspace?: Workspace;
    users?: Array<User>;
    userTemplates?: Array<UserTemplate>;
    organizationalUnits?: Array<OrganizationalUnit>;
    organizationalUnitTemplates?: Array<OrganizationalUnitTemplate>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    columnId?: string;
    eventTemplateId?: Uuid;
    context?: ActivityTimelineResolverContext;
};

type ResolvedActivityColumn =
    | {
          source: "journey";
          column: UdoColumn;
      }
    | {
          source: "event";
          column: UdoColumn;
          eventTemplateId: Uuid;
      };

function resolveActivityColumnDefinition(getAdditionalColumns: CapabilityUdoColumnResolver,
    columnId: string,
    context: ActivityTimelineResolverContext,
    eventTemplateId?: Uuid,
): ResolvedActivityColumn | undefined {
    const journeyColumn = getAdditionalColumns(context.eventContainerTemplate).find(
        (column) => String(column.id) === columnId,
    );
    if (journeyColumn !== undefined) {
        return {source: "journey", column: journeyColumn};
    }

    if (eventTemplateId !== undefined) {
        const eventTemplate = context.eventTemplates.find((template) => template.id === eventTemplateId);
        const eventColumn = getAdditionalColumns(eventTemplate).find((column) => String(column.id) === columnId);
        if (eventColumn !== undefined) {
            return {source: "event", column: eventColumn, eventTemplateId: eventTemplateId};
        }
    }

    for (const eventTemplate of context.eventTemplates) {
        const eventColumn = getAdditionalColumns(eventTemplate).find((column) => String(column.id) === columnId);
        if (eventColumn !== undefined) {
            return {source: "event", column: eventColumn, eventTemplateId: eventTemplate.id};
        }
    }

    return undefined;
}

function resolveSelectOptionDisplayName(
    optionId: unknown,
    options: ReadonlyArray<{id: Uuid; displayName: string}>,
): string {
    if (optionId === null || optionId === undefined) {
        return "-";
    }
    const normalized = String(optionId).trim();
    if (normalized === "") {
        return "-";
    }
    const option = options.find((item) => String(item.id).toLowerCase() === normalized.toLowerCase());
    return option?.displayName ?? normalized;
}

function formatActivityUserReference(
    value: unknown,
    options: ActivityFieldFormatOptions,
): string | null {
    if (typeof value !== "string" || value.trim() === "") {
        return null;
    }
    const trimmed = value.trim();
    if (uuidZodValidator.safeParse(trimmed).success !== true) {
        return null;
    }
    if (options.users === undefined || options.userTemplates === undefined) {
        return null;
    }
    const displayName = getUserDisplayName(
        trimmed as Uuid,
        options.users,
        options.userTemplates,
        options.platformUserDetailsByUserId,
    );
    if (displayName === "-" || displayName === "?") {
        return null;
    }
    return displayName;
}

function geographyKindFromColumnLabel(label: string): "pincode" | "city" | "state" | "zone" | "country" | null {
    const normalized = label.trim().toLowerCase();
    if (normalized.includes("pincode") === true || normalized.includes("pin code") === true) {
        return "pincode";
    }
    if (normalized.includes("city") === true) {
        return "city";
    }
    if (normalized.includes("state") === true) {
        return "state";
    }
    if (normalized.includes("zone") === true) {
        return "zone";
    }
    if (normalized.includes("country") === true) {
        return "country";
    }
    return null;
}

function formatGeographyTypedColumnValue(
    value: unknown,
    columnType: string,
    geography: GeographyContextData | null | undefined,
): string | null {
    if (GEOGRAPHY_REFERENCE_COLUMN_TYPES.has(columnType) !== true) {
        return null;
    }
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    if (geography === null || geography === undefined) {
        return null;
    }
    const resolved = geography.resolveAdditionalColumnValue(columnType, value);
    if (resolved === null || resolved === undefined || resolved === "") {
        return typeof value === "string" ? value : "-";
    }
    return resolved;
}

function formatGeographyByColumnLabel(
    value: unknown,
    columnLabel: string,
    geography: GeographyContextData | null | undefined,
): string | null {
    if (geography === null || geography === undefined) {
        return null;
    }
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    if (uuidZodValidator.safeParse(trimmed).success !== true) {
        return null;
    }
    const kind = geographyKindFromColumnLabel(columnLabel);
    if (kind === null) {
        return null;
    }
    const id = trimmed as Uuid;
    if (kind === "pincode") {
        return geography.indiaPincodesById.get(id)?.pincode ?? null;
    }
    if (kind === "city") {
        return geography.indiaCitiesById.get(id)?.name ?? null;
    }
    if (kind === "state") {
        return geography.indiaStatesById.get(id)?.name ?? null;
    }
    if (kind === "zone") {
        return geography.indiaZonesById.get(id)?.name ?? null;
    }
    return geography.countriesById.get(id)?.name ?? null;
}

function formatActivityColumnTypedValue(
    value: unknown,
    resolvedColumn: ResolvedActivityColumn,
    options: ActivityFieldFormatOptions,
): string | null {
    const column = resolvedColumn.column;
    const geographyDisplay = formatGeographyTypedColumnValue(value, String(column.type), options.context?.geography);
    if (geographyDisplay !== null) {
        return geographyDisplay;
    }

    switch (column.type) {
        case UdoColumnType.user: {
            const displayName = formatActivityUserReference(value, options);
            return displayName ?? "-";
        }
        case UdoColumnType.userDefinedObject:
        case UdoColumnType.userDefinedObjects: {
            const udoOptions = options.context?.userDefinedObjectOptionsByTemplateId?.get(
                column.configuration.userDefinedObjectTemplateId,
            ) ?? [];
            const values = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];
            return values.map((item) => resolveSelectOptionDisplayName(item, udoOptions)).join(", ") || "-";
        }
        case UdoColumnType.ou: {
            return formatActivityOrganizationalUnitReference(value, options);
        }
        case UdoColumnType.timestamp: {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            return formatDateTime(String(value));
        }
        case UdoColumnType.number: {
            return value === null || value === undefined ? "-" : String(value);
        }
        case UdoColumnType.json:
        case UdoColumnType.boolean:
        case UdoColumnType.integer:
        case UdoColumnType.userTemplate:
        case UdoColumnType.ous: {
            return formatUserDefinedObjectAdditionalColumnValue(column, value, {
                resolveUserTemplate: id => options.userTemplates?.find(template => template.id === id)?.displayName ?? "?",
                resolveOrganizationalUnit: id => formatActivityOrganizationalUnitReference(id, options),
            });
        }
        case UdoColumnType.email:
        case UdoColumnType.geographyCountries:
        case UdoColumnType.geographyIndiaCities:
        case UdoColumnType.geographyIndiaPincodes:
        case UdoColumnType.geographyIndiaStates:
        case UdoColumnType.geographyIndiaZones:
        case UdoColumnType.geographyLatitude:
        case UdoColumnType.geographyLongitude:
        case UdoColumnType.image:
        case UdoColumnType.indianPhoneNumber:
        case UdoColumnType.product:
        case UdoColumnType.string:
        case UdoColumnType.uuid: {
            return null;
        }
    }
}

function formatActivityOrganizationalUnitReference(
    value: unknown,
    options: ActivityFieldFormatOptions,
): string {
    if (typeof value !== "string" || value.trim() === "") {
        return "-";
    }
    if (options.organizationalUnits === undefined || options.organizationalUnitTemplates === undefined) {
        return value.trim();
    }
    const ou = options.organizationalUnits.find((item) => item.id === value.trim());
    if (ou === undefined) {
        return value.trim();
    }
    const ouTemplate = options.organizationalUnitTemplates.find((template) => template.id === ou.templateId);
    if (ouTemplate === undefined) {
        return value.trim();
    }
    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
}

export function formatActivityColumnFieldValue(getAdditionalColumns: CapabilityUdoColumnResolver,
    value: unknown,
    columnId: string | undefined,
    options: ActivityFieldFormatOptions,
): string {
    if (
        columnId !== undefined &&
        options.context !== undefined
    ) {
        const resolvedColumn = resolveActivityColumnDefinition(getAdditionalColumns, columnId, options.context, options.eventTemplateId);
        if (resolvedColumn !== undefined) {
            const typedDisplay = formatActivityColumnTypedValue(value, resolvedColumn, options);
            if (typedDisplay !== null) {
                return typedDisplay;
            }
            const geographyByLabel = formatGeographyByColumnLabel(
                value,
                resolvedColumn.column.displayName,
                options.context.geography,
            );
            if (geographyByLabel !== null) {
                return geographyByLabel;
            }
        }
    }
    return formatActivityFieldValue(value, options);
}

export function formatActivityFieldValue(
    value: unknown,
    options?: ActivityFieldFormatOptions,
): string {
    if (value === null || value === undefined) {
        return "-";
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
            return "-";
        }
        const userDisplayName = formatActivityUserReference(trimmed, options ?? {});
        if (userDisplayName !== null) {
            return userDisplayName;
        }
        if (
            options?.organizationalUnits !== undefined &&
            options.organizationalUnitTemplates !== undefined &&
            uuidZodValidator.safeParse(trimmed).success === true
        ) {
            const ouDisplay = formatActivityOrganizationalUnitReference(trimmed, options);
            if (ouDisplay !== trimmed) {
                return ouDisplay;
            }
        }
        if (options?.context?.workspaceSpecific?.tryAmaronLevelColumns === true && uuidZodValidator.safeParse(trimmed).success === true) {
            const resolved = formatAmaronOptionIdForDisplay(trimmed);
            if (resolved !== trimmed) {
                return resolved;
            }
        }
        if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
            return formatDate(trimmed);
        }
        return formatRichTextForActivityDisplay(trimmed);
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (typeof value === "object") {
        const notesDisplay = formatNotesValueForDisplay(value);
        if (notesDisplay !== null) {
            return notesDisplay;
        }
    }
    if (Array.isArray(value)) {
        return value.map((item) => formatActivityFieldValue(item, options)).join(", ");
    }
    try {
        return JSON.stringify(value);
    } catch {
        return "[object]";
    }
}

export function buildOldNewContext(getAdditionalColumns: CapabilityUdoColumnResolver,
    oldValue: unknown,
    newValue: unknown,
    options?: ActivityFieldFormatOptions,
): string {
    const oldDisplay = formatActivityColumnFieldValue(getAdditionalColumns, oldValue, options?.columnId, options ?? {});
    const newDisplay = formatActivityColumnFieldValue(getAdditionalColumns, newValue, options?.columnId, options ?? {});
    if (oldDisplay === "-" && newDisplay === "-") {
        return "-";
    }
    if (oldDisplay === "-") {
        return newDisplay;
    }
    if (newDisplay === "-") {
        return oldDisplay;
    }
    return `${oldDisplay} → ${newDisplay}`;
}
